import {useEffect, useRef, useState} from 'react';
import {TokenService} from 'application/services/TokenService';
import {HttpTokenRepository} from 'infrastructure/repositories/TokenRepository';

export interface TokenState {
    loading: boolean;
    isReady: boolean;
    token?: string;
    tokenValid: boolean;
    campaignActive?: boolean;
    tokenStatus?: string;
    guide?: string;
    campaign: string;
    campaignStartDate?: string;
    campaignEndDate?: string;
    error?: Error;
    resolvedToken?: string;
    resolvedAttempt?: number;
}

/**
 * useToken
 * @param pendingToken token a validar (puede ser undefined)
 * @param refreshKey forzar revalidación externa (conservar tu comportamiento actual)
 * @param attempt reintentos del mismo token (para volver a validar si el usuario borra y reescribe el mismo token)
 */
export const useToken = (
    pendingToken?: string,
    refreshKey: number = 0,
    attempt: number = 0
): TokenState => {
    const [state, setState] = useState<TokenState>({
        loading: !!pendingToken,
        isReady: false,
        token: pendingToken,
        tokenValid: false,
        campaignActive: false,
        campaign: '',
    });

    const tokenServiceRef = useRef(new TokenService(new HttpTokenRepository()));
    const reqIdRef = useRef(0);

    useEffect(() => {
        if (!pendingToken) {
            setState({
                loading: false,
                isReady: true,
                token: '',
                tokenValid: false,
                campaignActive: false,
                campaign: '',
                resolvedToken: undefined,
                resolvedAttempt: undefined,
            });
            return;
        }

        let cancelled = false;
        const myReqId = ++reqIdRef.current;

        setState((s) => ({
            ...s,
            loading: true,
            isReady: false,
            resolvedToken: undefined,
            resolvedAttempt: undefined,
        }));

        (async () => {
            try {
                const res = await tokenServiceRef.current.validateToken(pendingToken);
                if (cancelled) return;
                if (reqIdRef.current !== myReqId) return;

                setState({
                    loading: false,
                    isReady: true,
                    token: pendingToken,
                    tokenValid: res.tokenValid,
                    campaignActive: !!res.campaignActive,
                    tokenStatus: res.tokenStatus,
                    guide: res.guide,
                    campaign: res.campaign ?? '',
                    campaignStartDate: res.campaignStartDate,
                    campaignEndDate: res.campaignEndDate,
                    resolvedToken: pendingToken,
                    resolvedAttempt: attempt,
                });
            } catch (err) {
                if (cancelled) return;
                if (reqIdRef.current !== myReqId) return;

                setState({
                    loading: false,
                    isReady: true,
                    token: pendingToken,
                    tokenValid: false,
                    campaignActive: false,
                    tokenStatus: 'INVALID',
                    guide: undefined,
                    campaign: '',
                    campaignStartDate: undefined,
                    campaignEndDate: undefined,
                    error: err as Error,
                    resolvedToken: pendingToken,
                    resolvedAttempt: attempt,
                });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [pendingToken, refreshKey, attempt]);

    return state;
};
